/** What the agent saved (its own data store) and forwarded (leads from the owner's tests),
 *  so the owner can see it really happened. Test leads are real platform leads (Leads
 *  inbox, notification), marked as tests. */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase, faInbox, faRotate, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../confirmationModal';
import { guardWrite } from '../../utils/permissions';
import { pretty, studioApi, timeAgo } from './studioApi';
import { Badge, Empty } from './ui';

const HIDDEN_LEAD_KEYS = new Set(['via', 'category']); // shown as the badge / internal

export default function DataPanel({ botId, version }) {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);

  const load = useCallback(() => {
    studioApi.get(botId, '/data').then(setData).catch((e) => setError(e.message || String(e)));
  }, [botId]);
  useEffect(() => { load(); }, [load, version]);

  if (error) return <div className="as-pad"><div className="as-error">{error}</div></div>;
  if (!data) return <div className="as-pad as-muted">{t('agent_studio_loading')}</div>;

  const groups = new Map();
  for (const r of data.records) groups.set(r.collection, [...(groups.get(r.collection) || []), r]);
  const leads = data.test_leads || [];
  const hasTest = data.records.some((r) => r.is_test) || leads.length > 0;

  const run = async (fn) => {
    setActionError('');
    try {
      await fn();
    } catch (e) {
      setActionError(e.message || String(e));
    }
    load();
  };
  const remove = (id) => {
    if (!guardWrite()) return;
    run(() => studioApi.del(botId, `/records/${encodeURIComponent(id)}`));
  };

  return (
    <div className="as-pad as-data">
      <div className="as-row-between">
        <p className="as-small as-muted">{t('agent_studio_data_intro')}</p>
        <div className="as-row">
          <button type="button" className="as-icon-btn" onClick={load} title={t('agent_studio_refresh')}>
            <FontAwesomeIcon icon={faRotate} />
          </button>
          {hasTest && (
            <button type="button" className="as-btn as-btn-ghost as-btn-sm"
              onClick={() => guardWrite() && setConfirmClear(true)}>
              {t('agent_studio_data_clear')}
            </button>
          )}
        </div>
      </div>
      <ConfirmationModal open={confirmClear} onClose={() => setConfirmClear(false)}
        onConfirm={() => run(() => studioApi.post(botId, '/clear-test-data'))} itemName={t('agent_studio_data_clear_item')} />

      {actionError && <div className="as-error">{t('agent_studio_action_failed', { error: actionError })}</div>}

      <div className="as-data-head">
        <div className="as-data-title"><FontAwesomeIcon icon={faInbox} /> {t('agent_studio_data_test_leads')}</div>
        <Link className="as-btn-portal" to="/activity/leads">{t('agent_studio_data_leads_link')}</Link>
      </div>
      <p className="as-small as-muted as-data-note">{t('agent_studio_data_leads_hint')}</p>
      {leads.length === 0 ? <Empty>{t('agent_studio_data_test_leads_empty')}</Empty> : (
        <ul className="as-records">
          {leads.map((l) => (
            <li key={l.id} className="as-record">
              <div className="as-row">
                {l.params?.category && <Badge tone="brand">{l.params.category}</Badge>}
                <Badge>{t('agent_studio_test_badge')}</Badge>
                <span className="as-small as-muted as-push">{timeAgo(l.date, t, i18n.language)}</span>
              </div>
              <dl className="as-record-fields">
                {Object.entries(l.params || {}).filter(([k, v]) => !HIDDEN_LEAD_KEYS.has(k) && v !== '' && v != null).map(([k, v]) => (
                  <div key={k} className="as-record-field">
                    <dt>{pretty(k)}</dt>
                    <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      )}

      <div className="as-data-title as-data-title-next"><FontAwesomeIcon icon={faDatabase} /> {t('agent_studio_data_saved')}</div>
      {groups.size === 0 ? <Empty>{t('agent_studio_data_empty')}</Empty> : [...groups.entries()].map(([coll, recs]) => (
        <div key={coll} className="as-data-group">
          <div className="as-small as-muted">{pretty(coll)} · {recs.length}</div>
          <ul className="as-records">
            {recs.map((r) => (
              <li key={r.id} className="as-record">
                <div className="as-row">
                  <span className="as-mono"><strong>{r.id}</strong></span>
                  {r.is_test && <Badge>{t('agent_studio_test_badge')}</Badge>}
                  <span className="as-small as-muted as-push">{timeAgo(r.created_at, t, i18n.language)}</span>
                  <button type="button" className="as-icon-btn" onClick={() => remove(r.id)} title={t('agent_studio_delete')}>
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
                <dl className="as-record-fields">
                  {Object.entries(r.data || {}).map(([k, v]) => (
                    <div key={k} className="as-record-field">
                      <dt>{pretty(k)}</dt>
                      <dd>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
